import { ArrowLeftIcon } from "@heroicons/react/24/solid";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import Card from "../components/Card";
import { Button } from "../components/Button";

// Step components
const StepIndicator = ({ currentStep }: { currentStep: number }) => {
  const steps = [
    { title: "Extratos bancários" },
    { title: "Faturas de cartão" },
    { title: "Revisão" }
  ];

  return (
    <div className="mb-8 max-w-md mx-auto">
      <div className="flex justify-between w-full">
        {steps.map((step, index) => (
          <div key={index} className="flex flex-col items-center w-24">
            {/* Step number/check circle */}
            <div className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-medium 
              ${index < currentStep 
                ? "bg-green-500 text-white" 
                : index === currentStep 
                  ? "bg-blue-600 text-white" 
                  : "bg-gray-200 text-gray-500"}`}>
              {index < currentStep ? "✓" : index + 1}
            </div>
            
            {/* Step title */}
            <span className={`text-xs font-medium text-center mt-2 
              ${currentStep === index ? "text-blue-600" : "text-gray-500"}`}>
              {step.title}
            </span>
          </div>
        ))}
      </div>
      
      {/* Connection lines */}
      <div className="flex justify-center -mt-4 px-10">
        <div className={`h-0.5 w-1/2 ${0 < currentStep ? "bg-green-500" : "bg-gray-200"}`}></div>
        <div className={`h-0.5 w-1/2 ${1 < currentStep ? "bg-green-500" : "bg-gray-200"}`}></div>
      </div>
    </div>
  );
};

// Bank Statement Import Step
const BankStatementStep = ({ onNext }: { onNext: () => void }) => {
  return (
    <div className="space-y-6">
      <div className="mb-4">
        <h2 className="text-primaryText font-primary text-secondary mb-2">Importe seu extrato bancário</h2>
        <p className="text-secondaryText text-xs">Selecione o arquivo de extrato do seu banco para importar as transações.</p>
      </div>
      
      <div className="border-2 border-dashed border-blue-200 rounded-lg p-8 bg-blue-50 flex flex-col items-center justify-center">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-10 h-10 text-blue-500 mb-3">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
        </svg>
        <p className="text-primaryText text-sm font-medium mb-1">Arraste seu arquivo ou clique para selecionar</p>
        <p className="text-secondaryText text-xs mb-3">Formatos suportados: CSV, OFX</p>
        <button className="bg-blue-600 text-white text-xs font-medium py-2 px-4 rounded-md hover:bg-blue-700">
          Selecionar arquivo
        </button>
      </div>
      
      <div className="flex justify-end mt-6">
        <Button 
          title="Continuar" 
          onClick={onNext}
          className="font-medium px-6"
        />
      </div>
    </div>
  );
};

// Credit Card Import Step
const CreditCardStep = ({ onNext, onBack }: { onNext: () => void, onBack: () => void }) => {
  return (
    <div className="space-y-6">
      <div className="mb-4">
        <h2 className="text-primaryText font-primary text-secondary mb-2">Importe sua fatura de cartão de crédito</h2>
        <p className="text-secondaryText text-xs">Selecione o arquivo da fatura do seu cartão de crédito para importar as transações.</p>
      </div>
      
      <div className="border-2 border-dashed border-blue-200 rounded-lg p-8 bg-blue-50 flex flex-col items-center justify-center">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-10 h-10 text-blue-500 mb-3">
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z" />
        </svg>
        <p className="text-primaryText text-sm font-medium mb-1">Arraste sua fatura ou clique para selecionar</p>
        <p className="text-secondaryText text-xs mb-3">Formatos suportados: CSV, OFX, PDF</p>
        <button className="bg-blue-600 text-white text-xs font-medium py-2 px-4 rounded-md hover:bg-blue-700">
          Selecionar arquivo
        </button>
      </div>
      
      <div className="flex justify-end mt-6 space-x-2">
        <Button 
          title="Voltar" 
          onClick={onBack}
          className="bg-gray-100 text-gray-700 hover:bg-gray-200 font-medium px-6"
        />
        <Button 
          title="Continuar" 
          onClick={onNext}
          className="font-medium px-6"
        />
      </div>
    </div>
  );
};

// Review Step
const ReviewStep = ({ onBack, onFinish }: { onBack: () => void, onFinish: () => void }) => {
  return (
    <div className="space-y-6">
      <div className="mb-4">
        <h2 className="text-primaryText font-primary text-secondary mb-2">Revise suas transações</h2>
        <p className="text-secondaryText text-xs">Revise e categorize as transações importadas antes de finalizar.</p>
      </div>
      
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descrição</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valor</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Categoria</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            <tr>
              <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">01/04/2025</td>
              <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-900">Supermercado São João</td>
              <td className="px-4 py-3 whitespace-nowrap text-xs text-red-600">-R$ 156,78</td>
              <td className="px-4 py-3 whitespace-nowrap text-xs">
                <select className="border border-gray-300 rounded-md text-xs py-1 px-2 bg-white">
                  <option>Alimentação</option>
                  <option>Moradia</option>
                  <option>Transporte</option>
                  <option>Lazer</option>
                </select>
              </td>
            </tr>
            <tr>
              <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">01/04/2025</td>
              <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-900">Salário</td>
              <td className="px-4 py-3 whitespace-nowrap text-xs text-green-600">R$ 3.500,00</td>
              <td className="px-4 py-3 whitespace-nowrap text-xs">
                <select className="border border-gray-300 rounded-md text-xs py-1 px-2 bg-white">
                  <option>Salário</option>
                  <option>Investimento</option>
                  <option>Renda Extra</option>
                </select>
              </td>
            </tr>
            <tr>
              <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">02/04/2025</td>
              <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-900">Farmácia Popular</td>
              <td className="px-4 py-3 whitespace-nowrap text-xs text-red-600">-R$ 45,90</td>
              <td className="px-4 py-3 whitespace-nowrap text-xs">
                <select className="border border-gray-300 rounded-md text-xs py-1 px-2 bg-white">
                  <option>Saúde</option>
                  <option>Cuidados Pessoais</option>
                  <option>Outros</option>
                </select>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <div className="flex justify-end mt-6 space-x-2">
        <Button 
          title="Voltar" 
          onClick={onBack}
          className="bg-gray-100 text-gray-700 hover:bg-gray-200 font-medium px-6"
        />
        <Button 
          title="Finalizar importação" 
          onClick={onFinish}
          className="font-medium px-6"
        />
      </div>
    </div>
  );
};

// Main component
export default function ImportAssistantPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  
  const handleNext = () => {
    setCurrentStep(currentStep + 1);
  };
  
  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };
  
  const handleFinish = () => {
    navigate("/inicio");
  };
  
  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <BankStatementStep onNext={handleNext} />;
      case 1:
        return <CreditCardStep onNext={handleNext} onBack={handleBack} />;
      case 2:
        return <ReviewStep onBack={handleBack} onFinish={handleFinish} />;
      default:
        return null;
    }
  };
  
  return (
    <div className="flex flex-col w-full gap-4">
      <div className="flex items-center gap-2 mb-2">
        <Link to="/inicio" className="flex items-center text-gray-600 hover:text-blue-600 transition-colors text-xs">
          <ArrowLeftIcon className="h-4 w-4" />
          <span className="ml-1">Voltar para o início</span>
        </Link>
      </div>
      
      <Card className="p-0 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 py-6 px-6">
          <h1 className="text-xl font-primary font-semibold text-white mb-2">
            Importação de Dados Financeiros
          </h1>
          <p className="text-blue-100 text-xs font-text">
            Automatize o controle das suas finanças importando seus extratos bancários e faturas de cartão
          </p>
        </div>
        
        <div className="p-6">
          <StepIndicator currentStep={currentStep} />
          {renderStep()}
        </div>
      </Card>
    </div>
  );
}